import { ShieldX, LogOut, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { apiRequest } from '../lib/api';

interface AccessDeniedProps {
  walletAddress: string | null;
  error: string | null;
  onLogout: () => Promise<void>;
}

export default function AccessDenied({ walletAddress, error, onLogout }: AccessDeniedProps) {
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [bootstrapResult, setBootstrapResult] = useState<string | null>(null);

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const handleBootstrap = async () => {
    setIsBootstrapping(true);
    setBootstrapResult(null);

    try {
      const response = await apiRequest('POST', '/api/admin/bootstrap');
      setBootstrapResult(`Success! You are now ${response.role}. Refreshing...`);
      // Reload page after successful bootstrap
      setTimeout(() => window.location.reload(), 1500);
    } catch (err: any) {
      setBootstrapResult(err.message || 'Bootstrap failed');
    } finally {
      setIsBootstrapping(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 mb-4">
            <ShieldX className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-zinc-500 text-sm">
            You don't have permission to access the admin panel.
          </p>
        </div>

        {walletAddress && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 mb-4">
            <p className="text-zinc-400 text-xs mb-1">Connected Wallet</p>
            <p className="text-white font-mono text-sm">{truncateAddress(walletAddress)}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {bootstrapResult && (
          <div className={`rounded-lg p-4 mb-4 ${
            bootstrapResult.includes('Success')
              ? 'bg-emerald-500/10 border border-emerald-500/20'
              : 'bg-red-500/10 border border-red-500/20'
          }`}>
            <p className={`text-sm ${
              bootstrapResult.includes('Success') ? 'text-emerald-400' : 'text-red-400'
            }`}>{bootstrapResult}</p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleBootstrap}
            disabled={isBootstrapping}
            className="w-full py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-4 w-4 ${isBootstrapping ? 'animate-spin' : ''}`} />
            {isBootstrapping ? 'Claiming Admin...' : 'Claim Admin (First User Only)'}
          </button>

          <button
            onClick={onLogout}
            className="w-full py-3 px-4 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            Disconnect Wallet
          </button>
        </div>

        <p className="text-center text-zinc-600 text-xs mt-6">
          If you believe you should have access, contact a super admin
          <br />
          to grant your wallet address the admin role.
        </p>
      </div>
    </div>
  );
}
